import React, { useCallback, useEffect, type ComponentType } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import { N30, N600 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import ToolTip from '@atlaskit/tooltip';
// eslint-disable-next-line jira/restricted/@atlassian+jira-common-styles
import { gridSize } from '@atlassian/jira-common-styles/src';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useNestedModalDetector } from '../../../controllers';
import { useEnterFocusState } from '../../../controllers/enter-focus-state';
import { TransitionState, useMinimizableModal } from '../../../controllers/use-minimizable-modal';
import type { AiEnabledModalHeaderProps, MinimizableModalView } from '../../../types';
import { MinimizeIcon } from './assets/minimize-icon';
import messages from './messages';

// Using a separate ViewChanger to ../../modal-header as this is subject to change
// easier to make changes here than constantly updating modal-header view changer
export const AiEnabledViewChanger = () => {
	const { view, setView, isTransitionInState, setShouldFocusChanger } = useMinimizableModal();
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { minimizedButtonRef, wrapperRef, onKeyDown } = useEnterFocusState();

	const onViewChange = useCallback(
		(newView: MinimizableModalView) => () => {
			if (!isTransitionInState([view], TransitionState.ENTERED)) return;
			setShouldFocusChanger(true);
			setView(newView);
			const analyticsEvent = createAnalyticsEvent({
				action: 'button clicked',
				actionSubject: newView,
			});
			fireUIAnalytics(analyticsEvent, {
				source: 'globalIssueCreateModal',
				previousGICViewState: view,
			});
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[setView, isTransitionInState],
	);

	const { shouldDisableViewChangerButtons, customDisabledViewChangerButtonsMessage } =
		useMinimizableModal();

	useEffect(() => {
		if (shouldDisableViewChangerButtons) {
			fireUIAnalytics(
				createAnalyticsEvent({
					action: 'disabled',
					actionSubject: 'view changer buttons',
				}),
			);
		}
	}, [
		createAnalyticsEvent,
		customDisabledViewChangerButtonsMessage,
		shouldDisableViewChangerButtons,
	]);

	useNestedModalDetector();

	return (
		<AiEnabledViewChangerWrapper
			tabIndex={-1}
			ref={wrapperRef}
			data-testid="minimizable-modal.ui.modal-container.ai-enabled-header.view-changer-wrapper"
		>
			<ToolTip
				data-testid="minimizable-modal.ui.modal-container.ai-enabled-header.tool-tip"
				position="top"
				content={
					shouldDisableViewChangerButtons
						? customDisabledViewChangerButtonsMessage || formatMessage(messages.minimizeDisabled)
						: formatMessage(messages.minimize)
				}
			>
				<AiEnabledMiniButtonWrapper>
					<ToolbarButton
						testId="minimizable-modal.ui.modal-container.ai-enabled-header.minimized-button"
						onClick={onViewChange('ai-enabled-minimized')}
						onKeyDown={onKeyDown}
						spacing="none"
						ref={minimizedButtonRef}
						isDisabled={shouldDisableViewChangerButtons}
						appearance="subtle"
					>
						<MinimizeIcon label={formatMessage(messages.minimize)} size="medium" />
					</ToolbarButton>
				</AiEnabledMiniButtonWrapper>
			</ToolTip>
		</AiEnabledViewChangerWrapper>
	);
};

export const AiEnabledHeader: ComponentType<AiEnabledModalHeaderProps> = ({
	children,
	...rest
}) => (
	<AiMiniHeaderWrapper {...rest}>
		{children}
		<AiEnabledViewChanger />
	</AiMiniHeaderWrapper>
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ToolbarButton = styled(Button)({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&&': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		margin: `${gridSize / 8}px`,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		padding: `${(gridSize / 8) * 3}px`,
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const AiEnabledMiniButtonWrapper = styled.div({
	lineHeight: 1,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const AiEnabledViewChangerWrapper = styled.div({
	display: 'flex',
	flexDirection: 'row',
	justifyContent: 'flex-end',
	flexShrink: 0,
	paddingRight: token('space.050', '4px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const AiMiniHeaderWrapper = styled.div({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	justifyContent: 'flex-end',
	color: token('color.text', N600),
	borderBottom: `0.5px solid ${token('color.border', N30)}`,
	boxShadow: `0 6px 5px -5px ${token('elevation.shadow.overflow.spread', N30)}`,
	margin: `0 ${token('space.150', '12px')}`,
	padding: `${token('space.250', '20px')} ${token('space.100', '8px')}`,
});
